/**
 * Authentication state — CLAUDE.md §6.7: what "signed in" means to the rest of the application,
 * and getting the Base Portal handoff out of the URL before anything can observe it.
 *
 * The context and hook sit here rather than beside the provider (`components/auth/AuthProvider.tsx`)
 * because a module exporting both a component and a hook breaks `react-refresh`.
 */

import { createContext, useContext } from 'react';

import { setAuthToken, type AuthUser, type Credentials } from './api';

/**
 * Four states, because the two failures are not the same failure: `anonymous` is no token or one
 * Base Portal rejected, while `unavailable` means Base Portal could not be reached and the
 * credential has not been shown to be invalid — never send that user to reset a working password.
 */
export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous' | 'unavailable';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** The handoff login name — good enough to greet with, never a stand-in for the verified `user`. */
  handoffLoginName: string | null;
  /** Exchanges a credential for a token. Rejects with an `ApiError` the caller must interpret. */
  signIn: (credentials: Credentials) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-checks a token after an `unavailable` result. */
  retryRestore: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth was called outside <AuthProvider>.');
  }
  return value;
}

/**
 * What Base Portal hands over in the URL. The names are `token` and `loginName`, read from the
 * legacy `BasePortalTokenFilter` rather than guessed — getting them wrong fails silently, landing
 * an already-signed-in user on the login form. `loginName` is optional and proves nothing.
 */
export interface BasePortalHandoff {
  token: string;
  loginName: string | null;
}

let handoff: BasePortalHandoff | null = null;
let consumed = false;

/** The two parameters Base Portal sends, wherever in the URL they turn up. */
const TOKEN_PARAM = 'token';
const LOGIN_NAME_PARAM = 'loginName';

/**
 * The handoff parameters carried in the URL *fragment*, or null if there are none. Base Portal
 * registers WinSight with a trailing `#/` and appends the query to that, so `location.search` is
 * empty and `URL.searchParams` sees nothing. Which side of the `#` they land on is Base Portal's
 * choice per registered link, so both are read. A fragment still reaches browser history and is
 * stripped below.
 */
function fragmentParams(hash: string): { path: string; params: URLSearchParams } | null {
  if (hash === '') {
    return null;
  }
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const queryStart = withoutHash.indexOf('?');
  if (queryStart === -1) {
    return null;
  }
  return {
    path: withoutHash.slice(0, queryStart),
    params: new URLSearchParams(withoutHash.slice(queryStart + 1)),
  };
}

/**
 * Take the handoff out of the URL. Called from `main.tsx` before the first render and before the
 * router reads the location: §6.7 requires the token be consumed into memory and stripped with
 * `history.replaceState` before anything else, since one left in the address bar reaches history,
 * access logs and `Referer` irretrievably. Idempotent — a second call cannot lose the token.
 */
export function consumeBasePortalHandoff(): void {
  if (consumed) {
    return;
  }
  consumed = true;

  const url = new URL(window.location.href);

  // The query string first — the documented shape, and the one the legacy filter reads.
  let token = url.searchParams.get(TOKEN_PARAM);
  let loginName = url.searchParams.get(LOGIN_NAME_PARAM);

  // Then the fragment, which is where the live Base Portal link actually puts them.
  const fragment = token === null && loginName === null ? fragmentParams(url.hash) : null;
  if (fragment !== null) {
    token = fragment.params.get(TOKEN_PARAM);
    loginName = fragment.params.get(LOGIN_NAME_PARAM);
  }

  if (token === null && loginName === null) {
    return;
  }

  if (token !== null && token !== '') {
    handoff = { token, loginName: loginName === null || loginName === '' ? null : loginName };
    setAuthToken(token);
  }

  // Strip both, whether or not the token was usable — `loginName` alone still identifies someone.
  url.searchParams.delete(TOKEN_PARAM);
  url.searchParams.delete(LOGIN_NAME_PARAM);

  let hash = url.hash;
  if (fragment !== null) {
    fragment.params.delete(TOKEN_PARAM);
    fragment.params.delete(LOGIN_NAME_PARAM);
    const remaining = fragment.params.toString();
    // `#/` is Base Portal's registered link shape, not a route — this app is on BrowserRouter, so
    // once the parameters are gone a bare `#/` is worth nothing and only misleads.
    hash =
      remaining === '' && (fragment.path === '' || fragment.path === '/')
        ? ''
        : `#${fragment.path}${remaining === '' ? '' : `?${remaining}`}`;
  }

  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${hash}`);
}

/** The consumed handoff, for the provider's initial state. Null when the user came directly. */
export function basePortalHandoff(): BasePortalHandoff | null {
  return handoff;
}

/** Cleared on sign-out so a stale login name cannot outlive the session it arrived with. */
export function clearBasePortalHandoff(): void {
  handoff = null;
}
